import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

/**
 * SkillsSelector - Skills selection with custom skill input support
 * Handles both predefined skills and custom "Other" skills
 * 
 * @param {Array} options - Array of {value, label} objects (includes 'other' option)
 * @param {Array} value - Array of selected skill values (can include custom skills)
 * @param {Function} onChange - Callback when selection changes
 * @param {number} max - Maximum number of selections allowed
 * @param {string} testId - Base data-testid for testing
 * @param {string} placeholder - Placeholder text when nothing selected
 * @param {boolean} disabled - Disable all interactions
 */
export const SkillsSelector = ({ 
  options = [], 
  value = [], 
  onChange, 
  max = 12,
  testId = 'skills-chips',
  placeholder = 'Select your skills',
  disabled = false
}) => {
  const { t } = useTranslation();
  const [customSkillInput, setCustomSkillInput] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  
  // Separate predefined skills from custom skills
  const predefinedSkillValues = options.map(opt => opt.value);
  const selectedPredefinedSkills = value.filter(v => predefinedSkillValues.includes(v));
  const customSkills = value.filter(v => !predefinedSkillValues.includes(v));
  
  const handleToggle = (optionValue) => {
    if (disabled) return;
    
    // Handle "other" option specially
    if (optionValue === 'other') {
      setShowCustomInput(!showCustomInput);
      return;
    }
    
    const isSelected = value.includes(optionValue);
    
    if (isSelected) {
      // Remove from selection
      const newValue = value.filter(v => v !== optionValue);
      onChange?.(newValue);
    } else {
      // Add to selection only if under max limit
      if (value.length < max) {
        const newValue = [...value, optionValue];
        onChange?.(newValue);
      }
    }
  };
  
  const handleAddCustomSkill = () => {
    if (!customSkillInput.trim() || disabled || value.length >= max) return;
    
    // Prevent duplicates
    const trimmedSkill = customSkillInput.trim();
    if (value.includes(trimmedSkill)) {
      setCustomSkillInput('');
      return;
    }
    
    const newValue = [...value, trimmedSkill];
    onChange?.(newValue);
    setCustomSkillInput('');
  };
  
  const handleRemoveCustomSkill = (customSkill) => {
    if (disabled) return;
    const newValue = value.filter(v => v !== customSkill);
    onChange?.(newValue);
  };
  
  const handleClearAll = () => {
    if (disabled) return;
    onChange?.([]);
    setShowCustomInput(false);
  };
  
  const isMaxReached = value.length >= max;
  
  return (
    <div className="space-y-3" data-testid={testId}>
      {/* Selection counter */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          {value.length === 0 ? placeholder : `${value.length} selected`}
        </span>
        <span className={`font-medium ${isMaxReached ? 'text-warning' : 'text-muted-foreground'}`}>
          {value.length}/{max}
        </span>
      </div>
      
      {/* Chips container */}
      <div className="flex flex-wrap gap-2" data-testid={`${testId}-container`}>
        {options.map((option) => {
          const isSelected = value.includes(option.value);
          const isOtherOption = option.value === 'other';
          const canSelect = !isSelected && !isMaxReached;
          
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => handleToggle(option.value)}
              disabled={disabled || (!isSelected && !isOtherOption && isMaxReached)}
              className={`
                inline-flex items-center gap-1.5 rounded-full px-3 h-9 
                border transition-colors duration-150 text-sm font-medium
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                disabled:opacity-50 disabled:cursor-not-allowed
                ${isOtherOption 
                  ? 'bg-accent text-accent-foreground border-accent hover:bg-accent/80'
                  : isSelected 
                    ? 'bg-primary text-primary-foreground border-transparent hover:bg-primary/90' 
                    : canSelect 
                      ? 'bg-secondary text-secondary-foreground border-border hover:bg-secondary/80'
                      : 'bg-muted text-muted-foreground border-border cursor-not-allowed'
                }
              `}
              data-testid={`chip-${option.value}`}
              aria-pressed={isSelected}
            >
              <span>{option.label}</span>
              {isSelected && !isOtherOption && (
                <X size={14} className="opacity-70" aria-hidden="true" />
              )}
              {isOtherOption && (
                <Plus size={14} className="opacity-70" aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>
      
      {/* Custom skill input */}
      {showCustomInput && (
        <div className="flex gap-2 items-end" data-testid="custom-skill-input-container">
          <div className="flex-1">
            <Input
              value={customSkillInput}
              onChange={(e) => setCustomSkillInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCustomSkill();
                }
              }}
              placeholder="Enter your custom skill..."
              disabled={disabled || isMaxReached}
              maxLength={50}
              data-testid="custom-skill-input"
            />
          </div>
          <Button
            type="button"
            onClick={handleAddCustomSkill}
            disabled={disabled || !customSkillInput.trim() || isMaxReached}
            size="sm"
            data-testid="add-custom-skill-button"
          >
            <Plus size={14} className="mr-1" />
            Add
          </Button>
        </div>
      )}
      
      {/* Custom skills display */}
      {customSkills.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">Custom Skills:</p>
          <div className="flex flex-wrap gap-2">
            {customSkills.map((customSkill) => (
              <button
                key={customSkill}
                type="button"
                onClick={() => handleRemoveCustomSkill(customSkill)}
                disabled={disabled}
                className="inline-flex items-center gap-1.5 rounded-full px-3 h-9 
                         bg-primary text-primary-foreground border-transparent 
                         hover:bg-primary/90 transition-colors text-sm font-medium
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid={`custom-chip-${customSkill}`}
              >
                <span>{customSkill}</span>
                <X size={14} className="opacity-70" />
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Clear all button */}
      {value.length > 0 && (
        <button
          type="button"
          onClick={handleClearAll}
          disabled={disabled}
          className="text-sm text-muted-foreground hover:text-foreground underline transition-colors"
          data-testid={`${testId}-clear-all`}
        >
          {t('profile.clearAll')}
        </button>
      )}
      
      {/* Max reached warning */}
      {isMaxReached && (
        <p className="text-xs text-warning flex items-center gap-1" data-testid={`${testId}-max-warning`}>
          <span className="font-semibold">{t('skills.maximumReached')}</span>
          <span>{t('skills.deselectToChoose')}</span>
        </p>
      )}
    </div>
  );
};

export default SkillsSelector;
